// Demandes.js — Version session29
// Fix: Modal "Créer le client" inline (évite déconnexion via window.location.href)
// Ajout: Associe automatiquement le client créé à la demande puis convertit en devis

import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Styles ────────────────────────────────────────────────────────────────────
const theme = {
  primary: '#1e40af', success: '#16a34a', danger: '#dc2626',
  warning: '#d97706', info: '#0891b2', border: '#e2e8f0',
  bg: '#f8fafc', textSecondary: '#64748b', textMuted: '#94a3b8',
  demande: {
    nouvelle:  { label: '🆕 Nouvelle',      color: '#3b82f6', bg: '#eff6ff' },
    en_cours:  { label: '🔧 En cours',       color: '#f59e0b', bg: '#fffbeb' },
    convertie: { label: '✅ Convertie',      color: '#16a34a', bg: '#f0fdf4' },
    rejetee:   { label: '❌ Rejetée',        color: '#dc2626', bg: '#fef2f2' },
  },
};

const styles = {
  page:         { padding: '32px', fontFamily: 'inherit' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 },
  titre:        { fontSize: 26, fontWeight: 900, color: '#0f172a', margin: 0 },
  sous:         { fontSize: 13, color: theme.textSecondary, marginTop: 4 },
  filtres:      { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  btnFiltre:    (active) => ({ padding: '6px 14px', borderRadius: 20, border: '1px solid', cursor: 'pointer', fontWeight: 600, fontSize: 12, borderColor: active ? theme.primary : theme.border, background: active ? '#eff6ff' : 'white', color: active ? theme.primary : theme.textSecondary }),
  card:         { background: 'white', borderRadius: 14, border: `1px solid ${theme.border}`, padding: '18px 20px', marginBottom: 12 },
  statutBadge:  { display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
  tag:          { display: 'inline-block', padding: '2px 8px', borderRadius: 12, background: '#f1f5f9', color: theme.textSecondary, fontSize: 11, fontWeight: 600 },
  btnSm:        { padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, width: '100%', marginBottom: 4 },
  btnPrimary:   { padding: '10px 20px', borderRadius: 10, border: 'none', background: theme.primary, color: 'white', fontWeight: 700, cursor: 'pointer' },
  input:        { width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  label:        { display: 'block', fontSize: 12, fontWeight: 700, color: theme.textSecondary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' },
  modal:        { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modalBox:     { background: 'white', borderRadius: 18, padding: 28, maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  vide:         { textAlign: 'center', padding: 48, color: theme.textMuted, fontWeight: 600 },
  loading:      { textAlign: 'center', padding: 32, color: theme.textSecondary },
};

const statutStyle = (s) => ({
  background: theme.demande[s]?.bg   || '#f1f5f9',
  color:      theme.demande[s]?.color || theme.textSecondary,
});

// ── Modal Créer Client pour demandes vitrine ──────────────────────────────────
const ModalCreerClient = ({ demande, token, onSuccess, onClose }) => {
  const [form, setForm] = useState({
    nom:         '',
    prenom:      '',
    email:       demande.email_contact || '',
    telephone:   '',
    adresse:     '',
    ville:       '',
    code_postal: '',
    numero_tva:  '',
    societe:     '',
    type_client: 'particulier',
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingClient, setExistingClient] = useState(null); // client déjà en DB

  // Extraire infos depuis description + vérifier si client existe déjà
  useEffect(() => {
    const desc = demande.description_besoin || '';
    
    // Extraire nom depuis [VITRINE] Prénom Nom <email>
    const nameMatch = desc.match(/\[VITRINE\]\s+([^<]+)\s*</);
    if (nameMatch) {
      const parts = nameMatch[1].trim().split(' ');
      if (parts.length >= 2) {
        setForm(f => ({ ...f, prenom: parts[0], nom: parts.slice(1).join(' ') }));
      } else {
        setForm(f => ({ ...f, nom: parts[0] }));
      }
    }

    const societeMatch = desc.match(/Société:\s*(.+)/);
    const tvaMatch     = desc.match(/TVA:\s*(.+)/);
    if (societeMatch) setForm(f => ({ ...f, societe: societeMatch[1].trim(), type_client: 'professionnel' }));
    if (tvaMatch)     setForm(f => ({ ...f, numero_tva: tvaMatch[1].trim() }));

    // Vérifier si l'email existe déjà comme CLIENT
    if (demande.email_contact) {
      setCheckingEmail(true);
      axios.get(`${API_URL}/clients?email=${encodeURIComponent(demande.email_contact)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(res => {
        const clients = res.data.clients || res.data || [];
        const found = clients.find(c =>
          c.email?.toLowerCase() === demande.email_contact.toLowerCase()
        );
        if (found) setExistingClient(found);
      }).catch(() => {}).finally(() => setCheckingEmail(false));
    }
  }, [demande, token]);

  // Associer client existant + convertir directement
  const useExistingClient = async () => {
    if (!existingClient) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/demandes/${demande.id_demande}`,
        { id_client: existingClient.id_client },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
      await axios.post(`${API_URL}/demandes/${demande.id_demande}/convertir`,
        { id_client: existingClient.id_client },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
    } catch(err) {
      setError(err.response?.data?.message || err.message);
    } finally { setSaving(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim()) {
      setError('Nom et email obligatoires.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // 1. Créer le client
      const res = await axios.post(`${API_URL}/clients`, form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newClient = res.data.client || res.data;
      const id_client = newClient?.id_client;

      if (!id_client) throw new Error('Création client échouée');

      // 2. Associer le client à la demande
      await axios.patch(`${API_URL}/demandes/${demande.id_demande}`,
        { id_client },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {}); // Non bloquant si la route n'existe pas

      // 3. Convertir en devis
      await axios.post(`${API_URL}/demandes/${demande.id_demande}/convertir`,
        { id_client },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onSuccess();
    } catch(err) {
      const msg = err.response?.data?.message || err.message || 'Erreur création client';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const ch = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>👤 Créer le client</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
              Demande vitrine de <strong>{demande.email_contact || '—'}</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: theme.textMuted }}>✕</button>
        </div>

        {/* Vérification email en cours */}
        {checkingEmail && (
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#0369a1' }}>
            🔍 Vérification si le client existe déjà…
          </div>
        )}

        {/* Client déjà existant → proposer de l'utiliser directement */}
        {existingClient && !checkingEmail && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: '#166534' }}>
              ✅ Client existant trouvé — {existingClient.prenom} {existingClient.nom}
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#15803d' }}>
              {existingClient.email}{existingClient.societe ? ` — ${existingClient.societe}` : ''}
            </p>
            <button onClick={useExistingClient} disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
              {saving ? '⏳…' : '🔗 Associer ce client et créer le devis'}
            </button>
            <span style={{ display: 'block', marginTop: 8, fontSize: 11, color: '#64748b' }}>
              ou remplissez le formulaire ci-dessous pour créer un nouveau client
            </span>
          </div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ flex: 1 }}><span style={styles.label}>Prénom</span>
              <input style={styles.input} value={form.prenom} onChange={ch('prenom')} placeholder="Prénom" /></label>
            <label style={{ flex: 1 }}><span style={styles.label}>Nom *</span>
              <input style={styles.input} value={form.nom} onChange={ch('nom')} placeholder="Nom *" required /></label>
          </div>

          <label><span style={styles.label}>Email *</span>
            <input style={styles.input} type="email" value={form.email} onChange={ch('email')} required /></label>

          <label><span style={styles.label}>Téléphone</span>
            <input style={styles.input} value={form.telephone} onChange={ch('telephone')} placeholder="+32 4xx xx xx xx" /></label>

          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ flex: 1 }}><span style={styles.label}>Type</span>
              <select style={styles.input} value={form.type_client} onChange={ch('type_client')}>
                <option value="particulier">Particulier</option>
                <option value="professionnel">Professionnel</option>
              </select>
            </label>
            {form.type_client === 'professionnel' && (
              <label style={{ flex: 1 }}><span style={styles.label}>TVA</span>
                <input style={styles.input} value={form.numero_tva} onChange={ch('numero_tva')} placeholder="BE 0xxx.xxx.xxx" /></label>
            )}
          </div>

          {form.type_client === 'professionnel' && (
            <label><span style={styles.label}>Société</span>
              <input style={styles.input} value={form.societe} onChange={ch('societe')} /></label>
          )}

          <label><span style={styles.label}>Adresse</span>
            <input style={styles.input} value={form.adresse} onChange={ch('adresse')} /></label>

          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ flex: 2 }}><span style={styles.label}>Ville</span>
              <input style={styles.input} value={form.ville} onChange={ch('ville')} /></label>
            <label style={{ flex: 1 }}><span style={styles.label}>Code postal</span>
              <input style={styles.input} value={form.code_postal} onChange={ch('code_postal')} /></label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={onClose}
              style={{ ...styles.btnPrimary, background: '#f1f5f9', color: '#334155' }}>
              Annuler
            </button>
            <button type="submit" disabled={saving}
              style={{ ...styles.btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? '⏳ Création + devis…' : '✅ Créer le client et le devis'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── CarteDemande ──────────────────────────────────────────────────────────────
const CarteDemande = ({ demande: d, token, actionLoading, onTraiter, onRejeter, onConvertir, onRefresh }) => {
  const isMobile = useIsMobile();
  const loading = actionLoading?.startsWith(String(d.id_demande));
  const [showModal, setShowModal] = useState(false);

  const handleModalSuccess = () => {
    setShowModal(false);
    onRefresh();
    alert('✅ Client créé et devis généré avec succès !');
  };

  return (
    <div style={{ ...styles.card, borderLeft: `4px solid ${theme.demande[d.statut]?.color || theme.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16,
        flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ flex: 1 }}>
          {/* En-tête statut */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ ...styles.statutBadge, ...statutStyle(d.statut) }}>
              {theme.demande[d.statut]?.label || d.statut}
            </span>
            {d.type_prestation && <span style={styles.tag}>🔧 {d.type_prestation}</span>}
            {d.source_demande === 'vitrine' && <span style={{ ...styles.tag, background: '#fef3c7', color: '#92400e' }}>🌐 Vitrine</span>}
            <span style={styles.tag}>📅 {new Date(d.date_creation).toLocaleDateString('fr-BE')}</span>
          </div>

          {/* Client */}
          <p style={{ margin: '0 0 6px 0', fontWeight: 700, fontSize: 15 }}>
            {d.Client ? (
              <>👤 {d.Client.prenom} {d.Client.nom}
                {d.Client.email && <span style={{ fontWeight: 400, fontSize: 13, color: theme.textSecondary }}> — {d.Client.email}</span>}
              </>
            ) : (
              <span style={{ color: '#d97706' }}>
                🌐 Visiteur vitrine
                {d.email_contact && <span style={{ fontWeight: 400, fontSize: 13, color: theme.textSecondary }}> — {d.email_contact}</span>}
                <span style={{ fontWeight: 400, fontSize: 11, color: theme.textMuted, marginLeft: 8 }}>(sans compte)</span>
              </span>
            )}
          </p>

          {/* Description */}
          <p style={{ margin: '0 0 8px 0', fontSize: 14, color: theme.textSecondary }}>
            {d.description_besoin}
          </p>

          {d.notes_admin && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#888', fontStyle: 'italic' }}>
              Note: {d.notes_admin}
            </p>
          )}
          {d.Devis && (
            <p style={{ margin: '6px 0 0', color: theme.success, fontSize: 13, fontWeight: 700 }}>
              ✅ Converti → Devis {d.Devis.numero_devis}
            </p>
          )}
        </div>

        {/* Actions */}
        {!['convertie', 'rejetee'].includes(d.statut) && (
          <div style={{ display: 'flex', gap: 6,
            flexDirection: isMobile ? 'row' : 'column',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            minWidth: isMobile ? 0 : 170,
            marginTop: isMobile ? 8 : 0 }}>
            {d.statut === 'nouvelle' && (
              <button onClick={onTraiter} disabled={loading}
                style={{ ...styles.btnSm, background: theme.info, color: 'white' }}>
                🔧 Prendre en charge
              </button>
            )}

            {/* Convertir — avec ou sans client */}
            {d.id_client ? (
              <button onClick={onConvertir} disabled={loading}
                style={{ ...styles.btnSm, background: theme.success, color: 'white' }}>
                📄 Créer un devis
              </button>
            ) : (
              <button onClick={() => setShowModal(true)}
                style={{ ...styles.btnSm, background: '#f59e0b', color: 'white' }}>
                👤 Créer client + devis
              </button>
            )}

            <button onClick={onRejeter} disabled={loading}
              style={{ ...styles.btnSm, background: theme.danger, color: 'white' }}>
              ✕ Rejeter
            </button>
          </div>
        )}
      </div>

      {/* Modal créer client */}
      {showModal && (
        <ModalCreerClient
          demande={d}
          token={token}
          onSuccess={handleModalSuccess}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// ── Page principale Admin ─────────────────────────────────────────────────────
const DemandesAdmin = ({ token }) => {
  const isMobile = useIsMobile();
  const [demandes, setDemandes]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filtre, setFiltre]             = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const fetchDemandes = useCallback(async () => {
    try {
      setLoading(true);
      const params = filtre ? `?statut=${filtre}` : '';
      const res = await axios.get(`${API_URL}/demandes${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemandes(res.data.data || res.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, filtre]);

  useEffect(() => { fetchDemandes(); }, [fetchDemandes]);

  const action = async (id, type, method = 'patch', body = {}) => {
    setActionLoading(`${id}-${type}`);
    try {
      await axios[method](`${API_URL}/demandes/${id}/${type}`, body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDemandes();
    } catch(e) {
      alert(e.response?.data?.message || `Erreur: ${type}`);
    } finally { setActionLoading(''); }
  };

  const counts = Object.fromEntries(
    Object.keys(theme.demande).map(s => [s, demandes.filter(d => d.statut === s).length])
  );

  return (
    <div style={{ ...styles.page, padding: isMobile ? '16px 12px' : '32px' }}>
      <div style={{ ...styles.header, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : undefined }}>
        <div>
          <h1 style={{ ...styles.titre, fontSize: isMobile ? 20 : 26 }}>📬 Traiter les demandes</h1>
          <p style={styles.sous}>{demandes.length} demande{demandes.length !== 1 ? 's' : ''} — dont {counts.nouvelle || 0} nouvelle{(counts.nouvelle || 0) !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchDemandes} style={{ ...styles.btnPrimary, background: '#f1f5f9', color: '#334155' }}>🔄 Actualiser</button>
      </div>

      <div style={styles.filtres}>
        <button style={styles.btnFiltre(!filtre)} onClick={() => setFiltre('')}>Toutes ({demandes.length})</button>
        {Object.entries(theme.demande).map(([k, v]) => (
          <button key={k} style={styles.btnFiltre(filtre === k)} onClick={() => setFiltre(filtre === k ? '' : k)}>
            {v.label} ({counts[k] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.loading}>⏳ Chargement…</div>
      ) : demandes.length === 0 ? (
        <div style={styles.vide}>Aucune demande{filtre ? ` — statut "${filtre}"` : ''}.</div>
      ) : (
        <div>
          {demandes.map(d => (
            <CarteDemande
              key={d.id_demande}
              demande={d}
              token={token}
              actionLoading={actionLoading}
              onRefresh={fetchDemandes}
              onTraiter={() => action(d.id_demande, 'traiter')}
              onRejeter={() => {
                const note = prompt('Motif du rejet (optionnel) :');
                if (note !== null) action(d.id_demande, 'rejeter', 'patch', { notes_admin: note });
              }}
              onConvertir={() => action(d.id_demande, 'convertir', 'post')}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Composant Client : soumettre une demande de devis ───────────────────────
const DemandesClient = ({ token }) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const preState  = location.state || {};
  const [form, setForm]     = useState({
    type_prestation:  preState.type_prestation || '',
    description_besoin: '',
    date_souhaitee:   '',
    note:             '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type_prestation.trim() || !form.description_besoin.trim()) {
      setError('Le type de prestation et la description sont obligatoires.');
      return;
    }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/demandes`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi. Réessayez.');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div style={{ padding:'clamp(16px,5vw,40px)', maxWidth:'520px', margin:'0 auto', textAlign:'center' }}>
        <div style={{ fontSize:'56px', marginBottom:'16px' }}>✅</div>
        <h2 style={{ color:'#0F6E56', fontWeight:'800', marginBottom:'8px' }}>Demande envoyée !</h2>
        <p style={{ color:'#64748b', marginBottom:'24px', lineHeight:1.6 }}>
          Votre demande a été transmise à notre équipe. Nous vous répondrons dans les plus brefs délais.
        </p>
        <div style={{ display:'flex', gap:'10px', justifyContent:'center', flexWrap:'wrap' }}>
          <button onClick={() => { setSuccess(false); setForm({ type_prestation:'', description_besoin:'', date_souhaitee:'', note:'' }); }}
            style={{ padding:'10px 20px', borderRadius:'8px', border:'1px solid #e2e8f0',
              background:'white', cursor:'pointer', fontWeight:'600', fontSize:'14px' }}>
            + Nouvelle demande
          </button>
          <button onClick={() => navigate('/dashboard')}
            style={{ padding:'10px 20px', borderRadius:'8px', border:'none',
              background:'#1A3A5C', color:'white', cursor:'pointer', fontWeight:'600', fontSize:'14px' }}>
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:'clamp(16px,5vw,32px)', maxWidth:'580px', margin:'0 auto' }}>
      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#1A3A5C,#2D6A9F)', borderRadius:'14px',
        padding:'20px 24px', color:'white', marginBottom:'24px' }}>
        <div style={{ fontSize:'28px', marginBottom:'6px' }}>📋</div>
        <h1 style={{ margin:0, fontSize:'clamp(18px,4vw,22px)', fontWeight:'800' }}>
          Demander un devis
        </h1>
        <p style={{ margin:'4px 0 0', opacity:.85, fontSize:'13px' }}>
          Décrivez votre besoin, notre équipe vous contacte rapidement.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
        {/* Type de prestation */}
        <div>
          <label style={{ display:'block', fontWeight:'700', fontSize:'13px',
            color:'#374151', marginBottom:'6px' }}>
            Type de prestation <span style={{ color:'#e74c3c' }}>*</span>
          </label>
          <input
            type="text"
            value={form.type_prestation}
            onChange={e => setForm(f => ({ ...f, type_prestation: e.target.value }))}
            placeholder="Ex: Bureau d'études, Énergie HT, Sécurité industrielle..."
            style={{ width:'100%', padding:'11px 14px', borderRadius:'8px',
              border:'1px solid #e2e8f0', fontSize:'14px', outline:'none',
              boxSizing:'border-box' }}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label style={{ display:'block', fontWeight:'700', fontSize:'13px',
            color:'#374151', marginBottom:'6px' }}>
            Description de votre besoin <span style={{ color:'#e74c3c' }}>*</span>
          </label>
          <textarea
            value={form.description_besoin}
            onChange={e => setForm(f => ({ ...f, description_besoin: e.target.value }))}
            placeholder="Décrivez précisément votre besoin, le contexte du projet..."
            rows={4}
            style={{ width:'100%', padding:'11px 14px', borderRadius:'8px',
              border:'1px solid #e2e8f0', fontSize:'14px', outline:'none',
              resize:'vertical', minHeight:'100px', boxSizing:'border-box' }}
            required
          />
        </div>

        {/* Date souhaitée */}
        <div>
          <label style={{ display:'block', fontWeight:'700', fontSize:'13px',
            color:'#374151', marginBottom:'6px' }}>
            Date souhaitée (optionnel)
          </label>
          <input
            type="date"
            value={form.date_souhaitee}
            onChange={e => setForm(f => ({ ...f, date_souhaitee: e.target.value }))}
            min={new Date().toISOString().split('T')[0]}
            style={{ width:'100%', padding:'11px 14px', borderRadius:'8px',
              border:'1px solid #e2e8f0', fontSize:'14px', outline:'none',
              boxSizing:'border-box' }}
          />
        </div>

        {/* Note */}
        <div>
          <label style={{ display:'block', fontWeight:'700', fontSize:'13px',
            color:'#374151', marginBottom:'6px' }}>
            Informations complémentaires (optionnel)
          </label>
          <textarea
            value={form.note}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
            placeholder="Budget approximatif, contraintes particulières, questions..."
            rows={3}
            style={{ width:'100%', padding:'11px 14px', borderRadius:'8px',
              border:'1px solid #e2e8f0', fontSize:'14px', outline:'none',
              resize:'vertical', boxSizing:'border-box' }}
          />
        </div>

        {error && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FCA5A5',
            borderRadius:'8px', padding:'12px 14px', color:'#B91C1C', fontSize:'13px' }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          <button type="button" onClick={() => navigate(-1)}
            style={{ flex:1, minWidth:'120px', padding:'12px',
              background:'white', border:'1px solid #e2e8f0',
              borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'14px' }}>
            ← Annuler
          </button>
          <button type="submit" disabled={loading}
            style={{ flex:2, minWidth:'180px', padding:'12px',
              background: loading ? '#94a3b8' : '#1A3A5C',
              color:'white', border:'none', borderRadius:'8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight:'700', fontSize:'14px' }}>
            {loading ? '⏳ Envoi en cours...' : '📤 Envoyer ma demande'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Export principal ──────────────────────────────────────────────────────────
export default function Demandes() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') ||
    (() => { try { return JSON.parse(localStorage.getItem('user'))?.token; } catch { return null; } })();
  const user  = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  if (user?.role === 'admin') return <DemandesAdmin token={token} />;
  return <DemandesClient token={token} />;
}
